import React from "react";
import { Sun, Moon, Check, Sparkles } from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export default function ThemeSelector() {
  const { theme, setTheme, palette, setPalette, palettes } = useTheme();
  const currentPalette = palettes.find((p) => p.id === palette) || palettes[0];
  const isDark = theme === "dark";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="theme-toggle"
          title="Customize Theme & Palette"
          className="group relative flex h-9 items-center gap-2 rounded-lg border border-border bg-card/80 px-2.5 text-muted-foreground transition-all duration-200 hover:border-primary/50 hover:bg-muted/60 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          {/* Active Palette Indicator Dot */}
          <span
            className="h-2.5 w-2.5 rounded-full transition-transform duration-200 group-hover:scale-110 shadow-sm"
            style={{
              backgroundColor: isDark ? currentPalette.accentDark : currentPalette.accentLight,
              boxShadow: `0 0 6px ${isDark ? currentPalette.accentDark : currentPalette.accentLight}80`,
            }}
          />

          {/* Mode Icon */}
          {isDark ? (
            <Sun className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45 text-foreground" />
          ) : (
            <Moon className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-12 text-foreground" />
          )}

          <span className="hidden text-xs font-medium md:inline-block text-foreground/80">
            {currentPalette.name.split(" ")[0]}
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-80 p-3 bg-card border border-border shadow-xl backdrop-blur-md rounded-xl animate-fade-up"
      >
        {/* Title */}
        <div className="flex items-center justify-between pb-2 mb-2.5 border-b border-border/80">
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <h4 className="text-xs font-bold uppercase tracking-wider text-foreground">
              Appearance & Palette
            </h4>
          </div>
          <span className="text-[10px] font-medium text-muted-foreground">
            {isDark ? "Night" : "Day"} · {currentPalette.name}
          </span>
        </div>

        {/* Section 1: Mode Switcher */}
        <div className="space-y-1.5 mb-3">
          <label className="text-[11px] font-semibold text-muted-foreground">
            Luminance Mode
          </label>
          <div className="grid grid-cols-2 gap-1.5 p-1 rounded-lg bg-muted/60 border border-border/50">
            <button
              type="button"
              onClick={() => setTheme("light")}
              className={`flex items-center justify-center gap-2 py-1.5 px-3 rounded-md text-xs font-semibold transition-all duration-150 ${
                !isDark
                  ? "bg-card text-foreground shadow-sm border border-border/80"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Sun className="h-3.5 w-3.5 text-amber-500" />
              <span>Day (Light)</span>
            </button>
            <button
              type="button"
              onClick={() => setTheme("dark")}
              className={`flex items-center justify-center gap-2 py-1.5 px-3 rounded-md text-xs font-semibold transition-all duration-150 ${
                isDark
                  ? "bg-card text-foreground shadow-sm border border-border/80"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Moon className="h-3.5 w-3.5 text-primary" />
              <span>Night (Dark)</span>
            </button>
          </div>
        </div>

        {/* Section 2: Palette Selection */}
        <div className="space-y-1.5">
          <label className="text-[11px] font-semibold text-muted-foreground">
            Color Palette
          </label>
          <div className="space-y-1">
            {palettes.map((p) => {
              const active = p.id === palette;
              const accentColor = isDark ? p.accentDark : p.accentLight;
              const bgColor = isDark ? p.bgDark : p.bgLight;

              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPalette(p.id)}
                  className={`w-full flex items-center justify-between p-2 rounded-lg text-left transition-all duration-150 border ${
                    active
                      ? "border-primary/60 bg-primary/10 shadow-sm"
                      : "border-transparent hover:border-border hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    {/* Visual Swatch Pill */}
                    <div
                      className="relative h-6 w-8 rounded-md border border-border/80 overflow-hidden flex-shrink-0 shadow-inner flex"
                      style={{ backgroundColor: bgColor }}
                    >
                      <div
                        className="w-1/2 h-full"
                        style={{ backgroundColor: bgColor }}
                      />
                      <div
                        className="w-1/2 h-full"
                        style={{ backgroundColor: accentColor }}
                      />
                    </div>

                    <div className="min-w-0">
                      <p className={`text-xs font-semibold truncate ${active ? "text-primary" : "text-foreground"}`}>
                        {p.name}
                      </p>
                      <p className="text-[10px] text-muted-foreground truncate">
                        {p.description}
                      </p>
                    </div>
                  </div>

                  {active && (
                    <Check className="h-4 w-4 text-primary flex-shrink-0 ml-2" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
